import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import { READ_ALL_REVIEWS_URL } from "constant/shared-constants";
import { RootState } from "interfaces/generics.type";
import NoSsr from "modules/NoSsr";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { numberFormat } from "util/helper/numberFormat";
import styles from "./Sitejabber.module.scss";

const GoogleBadge = dynamic(() => import(/* webpackChunkName: "google-badge" */ "container/footer/GoogleBadge"), { ssr: false });

function Sitejabber({ showRatingsOnly = false, showGoogleBadgeProps = false }: { showRatingsOnly: boolean, showGoogleBadgeProps?: boolean }) {
  const { t } = useTranslation("product");
  // const [loading, infoData] = useApiDataLoadOnce(getSitejabberBusinessInfoApi);
  // const feedbackAltText = "PartsAvatar User Feedback and Ratings";
  const reviewsAltText = "Discover Our Products Through Customer Reviews";
  const [showGoogleBadge, setShowGoogleBadge] = useState(false);
  const { averageRating, totalReviewCount } = useSelector((store: RootState) => store.authenticationState);
  // const currentPageType = useSelector((state: RootState) => state.utils.currentPageType);
  const router = useRouter();

  useEffect(() => {
    const elements = document.getElementsByClassName("trustpilot-widget");
    Array.from(elements).forEach((element) => {
      window.Trustpilot?.loadFromElement(element);
    });
  }, []);

  const handleRouteChange = () => {
    const loadedScript = document.getElementById("merchantWidgetScript");
    if (loadedScript) {
      loadedScript.remove();
    }
    // Clean up global effects if necessary
    if (window.merchantwidget) {
      window.merchantwidget = null; // Reset the widget
    }
    const elem = document.getElementById("merchantwidget-iframe-wrapper");
    if (elem) {
      elem.style.display = "none";
    }
    setShowGoogleBadge(false);
  };

  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, []);

  function onClickGoogleBadge() {
    const elem = document.getElementById("merchantwidget-iframe-wrapper");
    if (elem) {
      elem.style.display = "block";
    } else {
      setShowGoogleBadge(true);
    }
  }

  return (
    <div className={`${styles.sitejabber_container}`}>
      <HideIf show={!showRatingsOnly}>
        <PAText
          fontSize={FONT_SIZE[3]}
          bold
          inlineBlock
          className={`${styles.main_heading} m-t-0 text-center`}
        >
          {t("sitejabber_tagline")}

        </PAText>
      </HideIf>
      <div className={`${styles.bottom_container} d-flex align-items-center`}>

        <div className={`${styles.sitejabber_left}`}>
          {/* <HideIf show={infoData?.overallRating && !loading}> */}
          {/* <div>
            <PAImage
              width={120}
              height={45}
              alt={feedbackAltText}
              title={feedbackAltText}
              src={getRelativeImagePath("/images/public/sitejabber-review-4.6.webp")}
              className="p-r-5"
            />
          </div> */}
          {/* </HideIf> */}
          <div className="d-flex align-items-center">
            <PAImage
              width={50}
              height={50}
              alt={reviewsAltText}
              title={reviewsAltText}
              src={getRelativeImagePath("/cms/pubicImages/images/google-logo-v2.webp")}
              className="p-r-5"
            />
            <div className="d-flex flex-column">
              <PAText inlineBlock color="alert-secondary">
                {Array.from({ length: 5 }, (_, index) => (
                  <i className={`fa fa-star ${styles.star}`} key={index} />
                ))}
              </PAText>
              <PAText fontSize={FONT_SIZE[0]} bold>
                {/* {`${infoData?.overallRating} stars | ${infoData?.totalReviews} reviews`} */}
                {t("reviews_rating", { starRating: averageRating, reviewCount: numberFormat(totalReviewCount) })}
              </PAText>
            </div>
          </div>

        </div>
        <HideIf show={!showRatingsOnly}>
          <div className="d-flex flex-column m-t-5">
            <span>{t("sitejabber_secondary_line")}</span>
            <Link
              href={READ_ALL_REVIEWS_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`${styles.viewAll} pointer`}
              title="PartAvatar Consumer Feedback"
            >

              {t("read_review")}
              <i className="fa fa-arrow-right p-l-10" aria-hidden="true" />

            </Link>
          </div>
        </HideIf>

      </div>
      <div className={styles.review_image_container}>
        <NoSsr>
          <div
            className="trustpilot-widget d-flex justify-content-center"
            data-locale="en-US"
            data-template-id="53aa8807dec7e10d38f59f32"
            data-businessunit-id="5dceae754be8620001ff60a0"
            data-style-height="calc(9 * var(--safari-root-font, 10px))"
            data-style-width="calc(10 * var(--safari-root-font, 10px))"
          >
            <a href="https://www.trustpilot.com/review/partsavatar.ca" target="_blank" rel="noopener noreferrer">Trustpilot</a>
          </div>
        </NoSsr>
        <div onClick={onClickGoogleBadge} style={{ cursor: "pointer" }} className="d-flex justify-content-center">
          <PAImage
            width={100}
            height={80}
            src={getRelativeImagePath("/cms/pubicImages/images/GoogleTopQualityStore.webp")}
            alt="google store badge"
            title="google store badge"
          />
        </div>
        {showGoogleBadge && showGoogleBadgeProps && (
          <GoogleBadge />
        )}
        <div className="d-flex justify-content-center">
          <PAImage
            width={100}
            height={80}
            src={getRelativeImagePath("/images/home/bbb-client-say.svg")}
            alt="BBB"
            title="BBB"
          />
        </div>
      </div>
    </div>
  );
}

export default memo(Sitejabber);
